import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import {
  Sparkles, CheckCircle2, ArrowRight, Calendar, Mail, Phone, User as UserIcon,
  Briefcase, Target, Clock, MessageSquare, Building2, ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_LABELS = {
  'business-jumpstart-accelerator': {
    name: 'Business Jumpstart Accelerator',
    duration: '3 Months',
    price: '$997 or 3 payments of $397',
    blurb: 'An accountability-based coaching program to take you from idea to launch with structure and consistency.',
  },
  'business-growth-partnership': {
    name: 'Business Growth Partnership',
    duration: '6 Months',
    price: '$1,997 or monthly payments',
    blurb: 'A long-term development partnership focused on implementation, systems, and sustainable growth.',
  },
  'visionary-ceo-mentorship': {
    name: 'Visionary CEO Mentorship',
    duration: '12 Months',
    price: '$4,997 or monthly payments',
    blurb: 'A high-level mentorship for serious entrepreneurs committed to leadership, scale, and expansion.',
  },
};

const STAGES = ['Just an idea', 'Pre-launch / planning', 'Recently launched (0-6 months)', 'Operating (6-24 months)', 'Established (2+ years)'];
const TIMELINES = ['Immediately (this week)', 'Within 2 weeks', 'Within 30 days', 'Within 60 days', 'Just exploring'];
const EMPLOYMENT = ['Full-time employed', 'Part-time employed', 'Self-employed', 'Between jobs', 'Student', 'Retired'];
const CONTACT_PREFS = ['Email', 'Phone Call', 'Text Message'];

export default function ProgramApplicationPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('program') || 'business-jumpstart-accelerator';
  const program = PROGRAM_LABELS[slug] || PROGRAM_LABELS['business-jumpstart-accelerator'];

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    business_name: '',
    current_stage: '',
    business_type: '',
    biggest_challenge: '',
    top_90_day_goals: '',
    employment_status: '',
    timeline: '',
    preferred_contact: '',
    additional_notes: '',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const required = ['first_name', 'last_name', 'email', 'phone', 'current_stage', 'biggest_challenge', 'top_90_day_goals', 'timeline', 'preferred_contact'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/program-applications`, {
        ...form,
        program_slug: slug,
        program_name: program.name,
      });
      setSubmitted(true);
      toast.success('Application received. David will reach out within 48 hours.');
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout title={`Application Received - ${program.name}`} description="Your coaching program application was received.">
        <div className="bg-[#FAFAFA] min-h-screen py-16 sm:py-24" data-testid="program-application-success">
          <div className="container-custom max-w-2xl text-center">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 shadow-xl shadow-slate-100">
              <div className="w-16 h-16 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={32} className="text-[#10B981]" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">Application Received</h1>
              <p className="text-[#475569] mb-6 leading-relaxed">
                Thank you for applying to the <strong className="text-[#0A0F1C]">{program.name}</strong>. David personally reviews every application and will reach out within 48 hours with next steps.
              </p>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5 text-left mb-7">
                <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">What Happens Next</div>
                <ol className="space-y-2 text-sm text-[#475569]">
                  <li><strong className="text-[#0A0F1C]">1.</strong> David reviews your application personally.</li>
                  <li><strong className="text-[#0A0F1C]">2.</strong> You receive an invitation to a discovery call to confirm fit.</li>
                  <li><strong className="text-[#0A0F1C]">3.</strong> If aligned, you receive onboarding & payment details.</li>
                </ol>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/schedule" className="btn-primary-orange inline-flex items-center justify-center gap-2" data-testid="success-book-call-cta">
                  <Calendar size={16} /> Book a Free Jumpstart Call
                </Link>
                <Link to="/" className="inline-flex items-center justify-center gap-2 bg-white border border-[#E2E8F0] text-[#0A0F1C] hover:bg-[#F8FAFC] font-bold px-5 py-2.5 rounded-lg transition-colors" data-testid="success-home-cta">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const inputCls = 'w-full px-4 py-2.5 bg-white border border-[#E2E8F0] rounded-lg text-[#0A0F1C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30 focus:border-[#1D4ED8] text-sm transition-colors';
  const labelCls = 'block text-xs font-bold text-[#0A0F1C] uppercase tracking-wider mb-1.5';

  return (
    <Layout title={`Apply - ${program.name}`} description={program.blurb}>
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="program-application-page">
        {/* HERO */}
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-12 sm:py-16">
          <div className="container-custom max-w-3xl">
            <Link to="/programs" className="text-xs text-[#94A3B8] hover:text-white inline-flex items-center gap-1 mb-4" data-testid="back-to-programs">
              <ArrowRight size={12} className="rotate-180" /> Back to Programs
            </Link>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-semibold rounded-full mb-3" data-testid="program-badge">
              <Sparkles size={12} className="text-[#FB923C]" /> Coaching Application
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight" data-testid="program-title">
              Apply for the <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">{program.name}</span>
            </h1>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#94A3B8] mb-4">
              <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#FB923C]" /> {program.duration}</span>
              <span className="inline-flex items-center gap-1.5"><Briefcase size={14} className="text-[#FB923C]" /> {program.price}</span>
            </div>
            <p className="text-base text-[#CBD5E1] leading-relaxed">{program.blurb}</p>
          </div>
        </section>

        {/* FORM */}
        <section className="container-custom max-w-3xl py-12 sm:py-16">
          <form onSubmit={handleSubmit} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-xl shadow-slate-100 overflow-hidden" data-testid="program-application-form">
            <div className="px-6 sm:px-10 py-7 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white">
              <h2 className="text-xl font-bold text-[#0A0F1C] mb-1">Tell David About Your Business</h2>
              <p className="text-sm text-[#475569]">This application takes about 3-5 minutes. All fields marked with * are required.</p>
            </div>

            <div className="px-6 sm:px-10 py-7 space-y-6">
              {/* Personal Info */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <UserIcon size={16} className="text-[#1D4ED8]" />
                  <h3 className="font-bold text-[#0A0F1C] text-sm uppercase tracking-wider">Your Info</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input type="text" className={inputCls} value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} required data-testid="field-first-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input type="text" className={inputCls} value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} required data-testid="field-last-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input type="email" className={inputCls} value={form.email} onChange={(e) => handleChange('email', e.target.value)} required data-testid="field-email" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input type="tel" className={inputCls} value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} required data-testid="field-phone" />
                  </div>
                </div>
              </div>

              {/* Business */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={16} className="text-[#1D4ED8]" />
                  <h3 className="font-bold text-[#0A0F1C] text-sm uppercase tracking-wider">Your Business</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Business Name (if any)</label>
                    <input type="text" className={inputCls} value={form.business_name} onChange={(e) => handleChange('business_name', e.target.value)} placeholder="Leave blank if pre-launch" data-testid="field-business-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Business Type / Industry</label>
                    <input type="text" className={inputCls} value={form.business_type} onChange={(e) => handleChange('business_type', e.target.value)} placeholder="e.g. Consulting, E-commerce" data-testid="field-business-type" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Current Business Stage *</label>
                    <select className={inputCls} value={form.current_stage} onChange={(e) => handleChange('current_stage', e.target.value)} required data-testid="field-current-stage">
                      <option value="">Select your current stage</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Employment Status</label>
                    <select className={inputCls} value={form.employment_status} onChange={(e) => handleChange('employment_status', e.target.value)} data-testid="field-employment">
                      <option value="">Select status (optional)</option>
                      {EMPLOYMENT.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Goals */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Target size={16} className="text-[#1D4ED8]" />
                  <h3 className="font-bold text-[#0A0F1C] text-sm uppercase tracking-wider">Goals & Fit</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Biggest Challenge Right Now *</label>
                    <textarea rows={3} className={inputCls} value={form.biggest_challenge} onChange={(e) => handleChange('biggest_challenge', e.target.value)} placeholder="What's the #1 thing blocking your progress?" required data-testid="field-challenge" />
                  </div>
                  <div>
                    <label className={labelCls}>Top Goals for the Next 90 Days *</label>
                    <textarea rows={3} className={inputCls} value={form.top_90_day_goals} onChange={(e) => handleChange('top_90_day_goals', e.target.value)} placeholder="What do you want to accomplish in the next 3 months?" required data-testid="field-goals" />
                  </div>
                </div>
              </div>

              {/* Logistics */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={16} className="text-[#1D4ED8]" />
                  <h3 className="font-bold text-[#0A0F1C] text-sm uppercase tracking-wider">Logistics</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Timeline to Start *</label>
                    <select className={inputCls} value={form.timeline} onChange={(e) => handleChange('timeline', e.target.value)} required data-testid="field-timeline">
                      <option value="">When do you want to begin?</option>
                      {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Preferred Contact Method *</label>
                    <select className={inputCls} value={form.preferred_contact} onChange={(e) => handleChange('preferred_contact', e.target.value)} required data-testid="field-preferred-contact">
                      <option value="">How should David follow up?</option>
                      {CONTACT_PREFS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Anything Else We Should Know?</label>
                    <textarea rows={3} className={inputCls} value={form.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} placeholder="Optional - share any context that will help David prepare." data-testid="field-notes" />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 sm:px-10 py-6 bg-[#F8FAFC] border-t border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 text-xs text-[#475569] flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-[#10B981] flex-shrink-0" />
                Your information is private and reviewed only by David.
              </div>
              <Button type="submit" disabled={submitting} className="btn-primary-orange inline-flex items-center justify-center gap-2 px-7 py-3 text-base" data-testid="submit-application-btn">
                {submitting ? 'Submitting...' : (<>Submit Application <ArrowRight size={16} /></>)}
              </Button>
            </div>
          </form>

          {/* Alt path */}
          <div className="text-center mt-8 text-sm text-[#475569]" data-testid="alt-cta">
            Not ready to apply? <Link to="/schedule" className="text-[#1D4ED8] font-semibold hover:underline">Book a FREE 20-min Jumpstart call</Link> first.
          </div>
        </section>
      </div>
    </Layout>
  );
}
